import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';

import defaultClient from '@src/api/clients/default';

const STATUS_COLOR = {
  delivered: 'success',
  skipped: 'default',
  declined: 'warning',
  rejected_data: 'error',
  rejected: 'warning',
  dead: 'error',
  undelivered: 'info',
};

// Last 50 leads and what happened when we delivered them (from each lead's
// own posting record), so a client can see deliveries without asking us.
export default function DeliveryLogCard({ clientSlug }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    if (!clientSlug) return;
    setLoading(true);
    setError('');
    try {
      const res = await defaultClient.request({ url: `app1/webhook-config/${clientSlug}/delivery-log/` });
      setRows(res?.data?.results || []);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not load the delivery log');
    } finally {
      setLoading(false);
    }
  }, [clientSlug]);

  useEffect(() => { if (open) load(); }, [open, load]);

  return (
    <Box sx={{ mt: 3 }}>
      <Stack direction="row" spacing={1} alignItems="center">
        <Typography variant="h6" fontWeight={700} sx={{ flex: 1 }}>Delivery Log</Typography>
        {open && (
          <Button size="small" startIcon={<RefreshIcon />} onClick={load} disabled={loading} sx={{ textTransform: 'none' }}>
            Refresh
          </Button>
        )}
        <Button size="small" variant="outlined" onClick={() => setOpen((v) => !v)} sx={{ textTransform: 'none' }}>
          {open ? 'Hide' : 'Show last 50 leads'}
        </Button>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        What happened when we sent each of your most recent leads.
      </Typography>
      {open && (
        <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'auto' }}>
          {error && <Alert severity="error" sx={{ m: 1 }}>{error}</Alert>}
          {loading ? (
            <Box sx={{ textAlign: 'center', py: 3 }}><CircularProgress size={24} /></Box>
          ) : rows.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>No leads yet.</Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Lead received</TableCell>
                  <TableCell>Lead</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Details</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {r.created_at ? new Date(r.created_at).toLocaleString() : '—'}
                    </TableCell>
                    <TableCell>{r.name || '—'}{r.zip_code ? ` · ${r.zip_code}` : ''}</TableCell>
                    <TableCell>
                      <Chip size="small" label={r.status_label} color={STATUS_COLOR[r.status] || 'default'} variant="outlined" />
                    </TableCell>
                    <TableCell>
                      <Tooltip title={r.response_excerpt || ''} placement="top-start">
                        <Typography variant="body2">{r.detail}</Typography>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Paper>
      )}
    </Box>
  );
}
